import { https } from "../http";
import store from "../store";
import {
  resetSkypointBoard,
  setSkypointBoardColumn,
  setSkypointBoardColumnLoading,
  setSkypointProject,
  setSkypointProjectLoading,
  setSkypointProjectStatus,
} from "../store/reducers/skypointProject";
import { showError } from "../utils";
import swal from "sweetalert";
import { getDashboardProjectTasksSkypoint } from "./dashboard";

// Tasks a board column loads at a time ("Load more" adds the next ones)
export const BOARD_PAGE_SIZE = 20;

// One page of one board column: { tasks, count }, count being every task in that status
const getBoardPage = async (status, page) => {
  const response = await https().get(
    `/skypointProject/${page}?status=${status}&limit=${BOARD_PAGE_SIZE}`
  );
  return response?.status === 200 ? response.data : null;
};

// One board column: page 1 replaces it, a later page is added below what it shows
const getSkypointBoardColumn = async (status, page = 1) => {
  try {
    store.dispatch(setSkypointBoardColumnLoading({ status, loading: true }));
    const data = await getBoardPage(status, page);
    store.dispatch(
      setSkypointBoardColumn({
        status,
        page,
        append: page > 1,
        tasks: data?.tasks,
        count: data?.count,
      })
    );
  } catch (error) {
    store.dispatch(setSkypointBoardColumnLoading({ status, loading: false }));
    showError(error?.response?.data?.message, error?.response?.status);
  }
};

// The first page of every status column the board hasn't loaded yet
const loadSkypointBoard = () => {
  const { skypointProjectStatus, skypointBoard } =
    store.getState().skypointProject;
  (skypointProjectStatus || []).forEach(({ id }) => {
    if (!skypointBoard[id]) getSkypointBoardColumn(id);
  });
};

// Reloads the board after a change, each column with as many pages as it shows. Does nothing
// while the board isn't open.
const refreshSkypointBoard = async () => {
  const { skypointProjectStatus, skypointBoard } =
    store.getState().skypointProject;
  if (!Object.keys(skypointBoard).length) return;
  await Promise.all(
    (skypointProjectStatus || []).map(async ({ id }) => {
      const pages = Math.max(skypointBoard[id]?.page || 1, 1);
      try {
        const answers = await Promise.all(
          Array.from({ length: pages }, (_, i) => getBoardPage(id, i + 1))
        );
        store.dispatch(
          setSkypointBoardColumn({
            status: id,
            page: pages,
            tasks: answers.flatMap((answer) => answer?.tasks || []),
            count: answers[answers.length - 1]?.count,
          })
        );
      } catch (error) {
        showError(error?.response?.data?.message, error?.response?.status);
      }
    })
  );
};

const clearSkypointBoard = () => store.dispatch(resetSkypointBoard());

const addSkypointProjectTask = async (data) => {
  try {
    let response = await https().post("/skypointProject", data);
    if (response?.status === 200) {
      swal({
        title: response?.data?.message,
        icon: "success",
        dangerMode: false,
      });
      getAllSkypointProjectTasks(1);
      refreshSkypointBoard();
      return response?.data;
    }
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
    return false;
  }
};

const getAllSkypointProjectTasks = async (
  page = 1,
  search = "",
  showLoading = true
) => {
  try {
    const strUrl = `/skypointProject/${page}${
      search ? `?search=${search}` : ""
    }`;
    if (showLoading) store.dispatch(setSkypointProjectLoading(true));
    let response = await https().get(strUrl);
    if (response?.status === 200) {
      store.dispatch(setSkypointProject(response?.data));
    }
  } catch (error) {
    store.dispatch(setSkypointProjectLoading(false));
    showError(error?.response?.data?.message, error?.response?.status);
  }
};

const updateSkypointProjectTask = async (data, callBack) => {
  try {
    let response = await https().put("/skypointProject", data);
    if (response?.status === 200) {
      // swal({
      //   title: response?.data?.message,
      //   icon: "success",
      //   dangerMode: false,
      // });
      if (callBack) callBack();
      else {
        getAllSkypointProjectTasks(1);
        refreshSkypointBoard();
      }
      getDashboardProjectTasksSkypoint();
      return response?.data;
    }
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
    return false;
  }
};

const deleteSkypointProjectTask = async (id) => {
  try {
    let response = await https().delete(`/skypointProject/${id}`);
    if (response?.status === 200) {
      swal({
        title: response?.data?.message || "",
        icon: "success",
        dangerMode: false,
      });
      getAllSkypointProjectTasks(1);
      refreshSkypointBoard();
      getDashboardProjectTasksSkypoint();
    }
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
  }
};

const addSkypointProjectTaskStatus = async (data) => {
  try {
    let response = await https().post("/skypointProject/add-status", data);
    if (response?.status === 200) {
      swal({
        title: response?.data?.message,
        icon: "success",
        dangerMode: false,
      });
      await getSkypointProjectTaskStatus();
      return response?.data;
    }
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
    return false;
  }
};

const getSkypointProjectTaskStatus = async () => {
  try {
    const strUrl = `/skypointProject/all-status`;
    let response = await https().get(strUrl);
    if (response?.status === 200) {
      store.dispatch(setSkypointProjectStatus(response?.data));
    }
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
  }
};

const deleteSkypointProjectTaskStatus = async (id) => {
  try {
    let response = await https().delete(`/skypointProject/status/${id}`);
    if (response?.status === 200) {
      swal({
        title: response?.data?.message || "Status deleted successfully",
        icon: "success",
        dangerMode: false,
      });
      await getSkypointProjectTaskStatus();
      // Its tasks moved to "to do"
      refreshSkypointBoard();
    }
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
  }
};

export {
  addSkypointProjectTask,
  getAllSkypointProjectTasks,
  getSkypointBoardColumn,
  loadSkypointBoard,
  refreshSkypointBoard,
  clearSkypointBoard,
  updateSkypointProjectTask,
  deleteSkypointProjectTask,
  addSkypointProjectTaskStatus,
  getSkypointProjectTaskStatus,
  deleteSkypointProjectTaskStatus,
};
